/**
 * The pieces the five URL Tracking screens share.
 *
 * Everything here is built from the workspace's own primitives — Card,
 * CardHead, BarRow, Pill, EmptyState — so the module reads as part of Client
 * Master rather than as something bolted on. The one thing with no existing
 * equivalent is the clicks-over-time chart, which is plain inline SVG: a
 * charting library for one line would be a dependency this app has so far
 * managed without.
 */
import { useEffect, useId, useRef, useState } from 'react';
import Icon from '../../lib/icons.jsx';
import { Card, CardHead, CardBody, BarRow, EmptyState, Pill } from '../../components/Primitives.jsx';
import { STATUS_TONE, SOURCE_ICON } from '../../constants/urltracking.js';
import { copy, shortenUrl } from '../../lib/urltracking.js';
import { useApp } from '../../store/AppContext.jsx';

/** Thousands separators, and an em dash rather than a bare 0 for "nothing". */
export const num = n => Number(n || 0).toLocaleString('en-US');

/** A percentage of a total that may be zero. */
export const pct = (n, total) => (total > 0 ? Math.round((Number(n) / total) * 100) : 0);

/**
 * A duration a person can read at a glance.
 *
 * `null` is not zero and must not print as "0s" — nothing reported and a
 * visitor who left instantly are different facts, and the whole point of the
 * time-on-page figures is that most clicks cannot report at all.
 */
export function duration(seconds) {
  if (seconds === null || seconds === undefined) return null;
  const n = Math.max(0, Math.round(Number(seconds)));
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  const s = n % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

/**
 * Where the click came from, said so a reader knows which kind of blank it is.
 *
 * Three different situations end up in this column and only one of them is a
 * shrug. A click from this machine has no country because a loopback address
 * has none to find; a click from a real visitor may still be unresolved
 * because nothing is configured to resolve it. Printing a bare dash for all
 * three is what makes a local test look broken when it is working perfectly.
 */
export function placeOf(c) {
  const named = [c.city, c.country].filter(Boolean).join(', ');
  if (named) return { text: named, hint: null };
  if (c.local) {
    return {
      text: 'Local',
      hint: 'Clicked from this machine — a private address has no country to look up'
    };
  }
  return {
    text: 'Unknown',
    hint: 'No country for this address. Behind Cloudflare this fills in on its own; '
      + 'otherwise set GEO_PROVIDER in backend/.env to look addresses up'
  };
}

/**
 * An address, with the loopback ones named rather than shown raw.
 *
 * `::1` is IPv6 for "this machine" and reads as a glitch to anybody who has
 * not met it. On a developer's laptop it is the address of every single click,
 * so it is worth translating; the real value stays on the title so nothing is
 * hidden.
 */
export function ipOf(c) {
  const raw = c.ip;
  if (!raw) return { text: '—', hint: 'No address recorded for this click' };
  if (raw === '::1' || raw === '127.0.0.1') return { text: 'localhost', hint: raw };
  return { text: raw, hint: c.local ? `${raw} — a private address` : raw };
}

/** A cell that shows a value and explains itself on hover. */
export const Told = ({ of, className }) => (
  <span className={className} title={of.hint || undefined}>{of.text}</span>
);

/**
 * A click's time, in the workspace's own timezone.
 *
 * Not the reader's. A campaign in Dubai happened at Dubai o'clock whether it
 * is being read from Dubai, London or a laptop whose clock is set to UTC, and
 * a dashboard whose timestamps move depending on who opens it cannot be
 * compared with anything. `S.tz` is the workspace setting; the browser's own
 * zone is only the fallback for a workspace that has not set one.
 */
export function clickTime(value, tz, withDate = true) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value || '—');

  const opts = { hour: '2-digit', minute: '2-digit' };
  if (tz) opts.timeZone = tz;

  /* Today gets the time alone — a column of identical dates says nothing. */
  const today = new Date().toLocaleDateString('en-GB', tz ? { timeZone: tz } : undefined);
  const sameDay = d.toLocaleDateString('en-GB', tz ? { timeZone: tz } : undefined) === today;

  if (sameDay || !withDate) return d.toLocaleTimeString('en-GB', opts);
  return d.toLocaleString('en-GB', { ...opts, day: 'numeric', month: 'short' });
}

/**
 * The same moment with its date, for the Details lists.
 *
 * Shares clickTime's clock deliberately. The two used to disagree inside one
 * sheet — Details showing UTC while Recent clicks showed the workspace zone —
 * which is the kind of thing that makes a reader distrust every number on the
 * screen, and rightly.
 */
export function clickStamp(value, tz) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    ...(tz ? { timeZone: tz } : {})
  });
}

/**
 * Time on the destination, with the number of clicks it was measured from.
 *
 * The denominator is not decoration. A destination without the beacon reports
 * nothing, so an average can easily rest on three clicks out of two thousand —
 * printing it alone would read as the campaign's time on page, which it is not.
 */
export function DwellTiles({ dwell, compact }) {
  const d = dwell || {};
  const measured = Number(d.measured || 0);

  if (!measured) {
    return (
      <p className="utnote">
        No time-on-site data yet. It is reported by the destination itself — add the beacon snippet
        to the site this link points at and visits start reporting how long they lasted.
      </p>
    );
  }

  const rows = [
    ['Average time on site', duration(d.averageSeconds)],
    ['Longest visit', duration(d.longestSeconds)],
    ['Left within 10s', num(d.bounced)],
    ['Measured', `${num(measured)} of ${num(d.clicks)}`]
  ];

  return (
    <>
      <div className="utstats">
        {rows.map(([label, value]) => (
          <div key={label}><b>{value}</b><span>{label}</span></div>
        ))}
      </div>
      {compact ? null : (
        <p className="utnote">
          Measured on {num(measured)} of {num(d.clicks)} clicks — only destinations carrying the beacon
          can report, so treat this as a sample rather than the whole campaign. Time runs across
          every page of the visit, not just the one the link landed on.
        </p>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ live */

/**
 * The live indicator.
 *
 * It reports how the screen is actually being updated, not that the feature
 * exists. "Live" means a stream is attached and clicks arrive as they happen;
 * "Polling" means the stream could not be established and the counters are
 * being asked for every few seconds instead — slower, still correct, and worth
 * saying out loud rather than dressing up as the same thing.
 *
 * Polling also stops while the tab is in the background, so the badge says so.
 * A dot that claims to be live while nothing is being fetched is a small lie
 * the rest of the numbers would inherit.
 */
const MODES = {
  stream: ['Live', 'on', 'Clicks are streaming in as they happen'],
  poll: ['Polling', 'poll', 'The live stream is unavailable — checking every few seconds instead'],
  connecting: ['Connecting', 'poll', 'Attaching to the live stream'],
  off: ['Idle', '', 'Not watching for clicks']
};

export function LiveDot({ mode = 'stream' }) {
  const [visible, setVisible] = useState(!document.hidden);

  useEffect(() => {
    const fn = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', fn);
    return () => document.removeEventListener('visibilitychange', fn);
  }, []);

  /* A stream keeps delivering to a background tab; polling does not, and the
     badge should only claim to be paused when something really has stopped. */
  const paused = mode === 'poll' && !visible;
  const [label, tone, title] = MODES[mode] || MODES.off;

  return (
    <span className={`utlive${paused ? '' : ` ${tone}`}`} title={paused ? 'Paused while this tab is in the background' : title}>
      <i />
      {paused ? 'Paused' : label}
    </span>
  );
}

/* ------------------------------------------------------------------ copy */

/** Copy the short URL — never the long destination, which is §22's whole point. */
export function CopyUrl({ url, label = 'Copy URL', compact }) {
  const [done, setDone] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const go = async e => {
    e.stopPropagation();
    const ok = await copy(url);
    setDone(ok ? 'Copied!' : 'Press Ctrl+C');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setDone(false), 1600);
  };

  if (compact) {
    return (
      <button type="button" title={done || label} onClick={go} className={done ? 'ok' : undefined}>
        <Icon name={done ? 'check' : 'file'} />
      </button>
    );
  }

  return (
    <button type="button" className={`btn${done ? ' ok' : ''}`} onClick={go}>
      <Icon name={done ? 'check' : 'file'} />{done || label}
    </button>
  );
}

/** The short link, shown as the thing to copy rather than as a link to follow. */
export function ShortUrl({ url, compact }) {
  return (
    <span className="utshort">
      <code title={url}>{String(url || '').replace(/^https?:\/\//, '')}</code>
      <CopyUrl url={url} compact={compact} />
    </span>
  );
}

/* ----------------------------------------------------------------- pills */

export const TrackStatus = ({ value }) => (
  <Pill tone={STATUS_TONE[value] || 'p-grey'}>{value || 'Draft'}</Pill>
);

/** A source, with the icon that fits it when one of ours does. */
export function SourcePill({ value }) {
  const key = String(value || '').toLowerCase();
  if (!key) return <span className="dim">—</span>;
  return <Pill tone="p-blue" icon={SOURCE_ICON[key]}>{value}</Pill>;
}

export const Destination = ({ url }) => (
  <span className="utdest" title={url}>{shortenUrl(url)}</span>
);

/* ----------------------------------------------------------------- chart */

/**
 * Clicks over time.
 *
 * Two series on one grid: total clicks, and the unique visitors inside them.
 * Drawn together because the gap between the lines is the interesting part —
 * a day where clicks climb and uniques do not is the same people coming back,
 * which is a different story from a day that reached more of them.
 *
 * Days with no clicks are filled in as zeros rather than left out. A line that
 * hops straight from Monday to Friday reads as five flat days at that height,
 * which is the opposite of what happened.
 */
export function ClicksChart({ series, days = 30, height = 190 }) {
  const gradient = useId();
  const points = fillDays(series || [], days);

  if (!points.length) {
    return <div className="utchart empty"><span className="muted">No clicks in this period.</span></div>;
  }

  const top = Math.max(1, ...points.map(p => p.clicks));
  const W = 1000;
  const H = height;
  const pad = { top: 12, right: 8, bottom: 22, left: 8 };
  const innerW = W - pad.left - pad.right;
  const innerH = H - pad.top - pad.bottom;

  const x = i => pad.left + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
  const y = v => pad.top + innerH - (v / top) * innerH;

  const line = key => points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(p[key]).toFixed(1)}`).join(' ');
  const area = `${line('clicks')} L${x(points.length - 1).toFixed(1)} ${pad.top + innerH} L${x(0).toFixed(1)} ${pad.top + innerH} Z`;

  const label = d => {
    const date = new Date(`${d}T00:00:00`);
    return Number.isNaN(date.getTime()) ? d : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="utchart">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Clicks over time">
        <defs>
          <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--pri)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--pri)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Four guides, so a height can be read off the chart rather than guessed. */}
        {[0, 0.25, 0.5, 0.75, 1].map(f => (
          <line
            key={f}
            className="utgrid"
            x1={pad.left} x2={W - pad.right}
            y1={pad.top + innerH * f} y2={pad.top + innerH * f}
          />
        ))}

        <path className="utarea" d={area} fill={`url(#${gradient})`} />
        <path className="utline" d={line('clicks')} />
        <path className="utline uniq" d={line('uniqueVisitors')} />

        {points.map((p, i) => (
          <circle key={p.day} className="utdot" cx={x(i)} cy={y(p.clicks)} r={p.clicks ? 4 : 0}>
            <title>{`${label(p.day)} — ${num(p.clicks)} clicks, ${num(p.uniqueVisitors)} unique`}</title>
          </circle>
        ))}
      </svg>

      <div className="utaxis">
        <span>{label(points[0].day)}</span>
        <span className="utkey">
          <i className="c" />Clicks
          <i className="u" />Unique
          <b>peak {num(top)}</b>
        </span>
        <span>{label(points[points.length - 1].day)}</span>
      </div>
    </div>
  );
}

/** Pad a sparse series out to one entry per day, ending today. */
function fillDays(series, days) {
  const byDay = new Map(series.map(p => [String(p.day).slice(0, 10), p]));
  if (!byDay.size) return [];

  const out = [];
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (days - 1));

  for (let d = new Date(start); d <= new Date(); d.setDate(d.getDate() + 1)) {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const hit = byDay.get(key);
    out.push({ day: key, clicks: Number(hit?.clicks || 0), uniqueVisitors: Number(hit?.uniqueVisitors || 0) });
  }
  /* Anything older than the window still counts — it is rare, and dropping a
     row because the window moved would make the totals disagree with the
     chart underneath them. */
  for (const [key, p] of byDay) {
    if (!out.some(o => o.day === key)) {
      out.unshift({ day: key, clicks: Number(p.clicks || 0), uniqueVisitors: Number(p.uniqueVisitors || 0) });
    }
  }
  return out;
}

/* ------------------------------------------------------------ breakdowns */

/**
 * One grouped breakdown as a list of bars.
 *
 * Both numbers are shown, because they answer different questions: clicks is
 * how much traffic a source sent, uniques is how many people that was. A
 * source that doubles one without the other is worth knowing about.
 */
export function Breakdown({ title, icon = 'chart', tone = 'g-blue', rows, note, empty = 'Nothing yet.' }) {
  /* One panel receiving something other than a list is a reason to draw
     nothing, not to take the whole analytics page down with it. */
  const list = Array.isArray(rows) ? rows : [];
  const top = Math.max(1, ...list.map(r => Number(r.clicks || 0)));

  return (
    <Card>
      <CardHead icon={icon} tone={tone} title={title} count={list.length || undefined} />
      <CardBody>
        {note ? <p className="utnote">{note}</p> : null}
        {list.length ? list.map(r => (
          <BarRow
            key={r.label}
            label={r.label}
            value={`${num(r.clicks)} · ${num(r.uniqueVisitors)} uniq`}
            pct={pct(r.clicks, top)}
          />
        )) : <p className="muted">{empty}</p>}
      </CardBody>
    </Card>
  );
}

/** Top links or campaigns, ranked. */
export function TopList({ title, icon, tone, rows, onOpen, empty = 'Nothing yet.' }) {
  const list = Array.isArray(rows) ? rows : [];
  return (
    <Card>
      <CardHead icon={icon} tone={tone} title={title} count={list.length || undefined} />
      <CardBody>
        {list.length ? (
          <ol className="uttop">
            {list.map((r, i) => (
              <li key={r.id}>
                <span className="rank">{i + 1}</span>
                <button
                  type="button"
                  className="lnkbtn"
                  onClick={onOpen ? () => onOpen(r) : undefined}
                  disabled={!onOpen}
                >
                  {r.name}
                </button>
                <b>{num(r.clicks)}</b>
              </li>
            ))}
          </ol>
        ) : <p className="muted">{empty}</p>}
      </CardBody>
    </Card>
  );
}

/* ------------------------------------------------------------ recipients */

/**
 * Who was sent a personalised link, and who acted on it.
 *
 * Four separate numbers, kept separate on purpose. §57 is worth restating
 * here because this is the one panel where the temptation is strongest:
 * recipients is how many people were given a link, clicked is how many of them
 * followed it, clicks is how many times in total, and unique visitors is an
 * estimate of the browsers behind those clicks. None of them is a delivery and
 * none of them is a conversion.
 */
export function Recipients({ data, onOpen, empty }) {
  /* The workspace timezone, so these read the same as every other time in
     the module rather than following whoever happens to be looking. */
  const { S } = useApp();
  const list = Array.isArray(data?.recipients) ? data.recipients : [];
  const t = data?.totals || {};

  return (
    <Card>
      <CardHead icon="users" tone="g-blue" title="Recipients" count={list.length || undefined} />
      <CardBody>
        {list.length ? (
          <>
            <div className="utstats">
              <div><b>{num(t.recipients)}</b><span>Sent a link</span></div>
              <div><b>{num(t.clicked)}</b><span>Clicked</span></div>
              <div><b>{num(t.neverClicked)}</b><span>Not yet</span></div>
              <div><b>{num(t.clicks)}</b><span>Clicks in total</span></div>
            </div>

            <div className="tscroll">
              <table>
                <thead>
                  <tr><th>Recipient</th><th>Clicks</th><th>Unique</th><th>First click</th><th>Last click</th></tr>
                </thead>
                <tbody>
                  {list.map(r => (
                    <tr
                      key={r.clientId}
                      className={r.clicks ? undefined : 'utquiet'}
                      onClick={onOpen ? () => onOpen(r) : undefined}
                    >
                      <td><b>{r.clientName}</b></td>
                      <td>{r.clicks ? <b>{num(r.clicks)}</b> : <span className="dim">—</span>}</td>
                      <td className="dim">{r.clicks ? num(r.uniqueVisitors) : '—'}</td>
                      <td className="dim">{r.firstClick ? clickTime(r.firstClick, S.tz) : '—'}</td>
                      <td className="dim">{r.lastClick ? clickTime(r.lastClick, S.tz) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="utnote">
              A recipient here is somebody a personalised link was generated for. It says nothing
              about whether the message carrying it was delivered or read — only whether the link
              was clicked.
            </p>
          </>
        ) : (
          <p className="muted">
            {empty || 'No personalised links yet. Open a tracking URL and generate one per recipient '
              + 'to see who clicked.'}
          </p>
        )}
      </CardBody>
    </Card>
  );
}


/* --------------------------------------------------------------- filters */

/** A text input with a preset list behind it — suggestions, never a fence. */
export function PresetInput({ value, onChange, options, placeholder, id }) {
  const listId = useId();
  return (
    <>
      <input
        id={id}
        list={listId}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
      />
      <datalist id={listId}>
        {options.map(o => <option key={o} value={o} />)}
      </datalist>
    </>
  );
}

/** The §34 filter strip, shared by the analytics screens. */
export function TrackFilter({ value, onChange, campaigns, children }) {
  const set = (k, v) => onChange({ ...value, [k]: v });

  return (
    <div className="utfilter">
      <div className="f">
        <label htmlFor="utf-from">From</label>
        <input id="utf-from" type="date" value={value.startDate || ''} onChange={e => set('startDate', e.target.value)} />
      </div>
      <div className="f">
        <label htmlFor="utf-to">To</label>
        <input id="utf-to" type="date" value={value.endDate || ''} onChange={e => set('endDate', e.target.value)} />
      </div>
      {campaigns ? (
        <div className="f">
          <label htmlFor="utf-camp">Campaign</label>
          <select id="utf-camp" value={value.campaignId || ''} onChange={e => set('campaignId', e.target.value)}>
            <option value="">All campaigns</option>
            {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      ) : null}
      {children}
      <button
        type="button"
        className="btn"
        onClick={() => onChange({})}
        disabled={!Object.values(value).some(Boolean)}
      >
        <Icon name="x" />Clear
      </button>
    </div>
  );
}

/* ----------------------------------------------------------------- state */

/** Loading, failed and empty, said the same way on every screen. */
export function TrackState({ loading, error, empty, children }) {
  if (error) {
    return (
      <Card>
        <CardBody>
          <EmptyState icon="alert" tone="g-red" title="Could not load">{error}</EmptyState>
        </CardBody>
      </Card>
    );
  }
  if (loading) return <p className="muted utload">Loading…</p>;
  if (empty) return empty;
  return children;
}

